import Button from "kustom-front/src/components/atoms/Button";
import React, { FunctionComponent, ReactElement } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import CartButtonSpinner from "@Components/atoms/cart/CartButtonSpinner";
import CartTileBanner from "@Components/atoms/cart/CartTileBanner";
import CartPromoCode from "@Components/molecules/cart/CartPromoCode";
import useCartContext from "@Hooks/useCartContext";
import { Price } from "@Types/api/price";
import CartTotalLine from "./CartTotalLine";
import "./style.scss";

interface Props extends ContainerProps {
  bannerTexts: string[];
  disabledSubmitOrder: boolean;
  shippingTotal: Price;
  subTotal: Price;
  total: Price;
}

interface ContainerProps {
  disabled: boolean;
  onClickBanner: () => void;
  onSubmitOrder: () => void;
  onUpdatePromoCode: (promoCode: string | undefined) => void;
  promoCodeErrorMessage: string | undefined;
}

export const CartCheckoutTotal: FunctionComponent<Props> = ({
  bannerTexts,
  disabled,
  disabledSubmitOrder,
  onClickBanner,
  onSubmitOrder,
  onUpdatePromoCode,
  promoCodeErrorMessage,
  shippingTotal,
  subTotal,
  total,
}): ReactElement => {
  const intl = useIntl();

  return (
    <div className="CartCheckoutTotal">
      <CartTotalLine
        disabled={disabled}
        label={intl.formatMessage({
          id: "cheerz_CartCheckoutTotal_subTotal",
        })}
        total={subTotal}
      />
      <CartTotalLine
        disabled={disabled}
        informativeMessage={intl.formatMessage({
          id: "cheerz_CartCheckoutTotal_delivery_information",
        })}
        label={intl.formatMessage({
          id: "cheerz_CartCheckoutTotal_delivery",
        })}
        total={shippingTotal}
      />
      <div className="CartCheckoutTotal-promocodeContainer">
        <div className="CartCheckoutTotal-promocode-separator" />
        <div className="CartCheckoutTotal-promocode-title">
          <span className="CartCheckoutTotal-promocode-title--bold">
            <FormattedMessage id="cheerz_CartCheckoutTotal_promocode_title" />{" "}
          </span>
          <span>
            <FormattedMessage id="cheerz_CartCheckoutTotal_promocode_info" />
          </span>
        </div>
        <CartPromoCode
          disabled={disabled}
          errorMessage={promoCodeErrorMessage}
          onUpdatePromoCode={onUpdatePromoCode}
        />
        <div className="CartCheckoutTotal-promocode-separator" />
      </div>
      <div className="CartCheckoutTotal-total">
        <CartTotalLine
          disabled={disabled}
          label={intl.formatMessage({
            id: "cheerz_CartCheckoutTotal_total",
          })}
          total={total}
        />
      </div>
      <div className="CartCheckoutTotal-confirm">
        <Button disabled={disabledSubmitOrder} type="primary" onClick={onSubmitOrder}>
          {disabled ? (
            <CartButtonSpinner />
          ) : (
            <FormattedMessage id="cheerz_CartCheckoutTotal_order" />
          )}
        </Button>
      </div>
      {disabledSubmitOrder && (
        <div className="CartCheckoutTotal-outOfStock">
          {intl.formatMessage({ id: "cheerz_CartCheckoutTotal_outOfStock" })}
        </div>
      )}
      <div className="CartCheckoutTotal-bannerContainer">
        <CartTileBanner onClick={onClickBanner} texts={bannerTexts} />
      </div>
    </div>
  );
};

const CartCheckoutTotalContainer: FunctionComponent<ContainerProps> = ({
  disabled,
  onClickBanner,
  onSubmitOrder,
  onUpdatePromoCode,
  promoCodeErrorMessage,
}) => {
  const { cart, promos } = useCartContext();

  const hasOutOfStockProduct = (): boolean =>
    cart.articles.some((article) => article.product.outOfStock === true);

  return (
    <CartCheckoutTotal
      bannerTexts={promos.map((promo) => promo.description)}
      disabled={disabled}
      disabledSubmitOrder={hasOutOfStockProduct()}
      onClickBanner={onClickBanner}
      onSubmitOrder={onSubmitOrder}
      onUpdatePromoCode={onUpdatePromoCode}
      promoCodeErrorMessage={promoCodeErrorMessage}
      shippingTotal={cart.shippingTotal}
      subTotal={cart.subTotal}
      total={cart.total}
    />
  );
};

export default CartCheckoutTotalContainer;
